/**
 * React Query hooks for Projects API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../services/projects';
import type {
  CreateProjectRequest,
  UpdateProjectRequest,
} from '../types/api';

// =============================================================================
// Query Keys
// =============================================================================

type ProjectListFilters = {
  status?: 'active' | 'inactive' | 'all' | Array<'active' | 'inactive'>;
  search?: string;
  limit?: number;
};

export const projectsKeys = {
  all: ['projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (filters?: ProjectListFilters) =>
    [...projectsKeys.lists(), filters ?? {}] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectsKeys.details(), id] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to fetch list of projects with multi-select status filter + search.
 */
export function useProjects(params?: ProjectListFilters) {
  return useQuery({
    queryKey: projectsKeys.list(params),
    queryFn: () => projectsApi.list(params),
    staleTime: 5000, // 5 seconds — keeps list fresh after create/navigate-back
  });
}

/**
 * Hook to fetch single project by ID
 */
export function useProject(projectId: string) {
  return useQuery({
    queryKey: projectsKeys.detail(projectId),
    queryFn: () => projectsApi.get(projectId),
    enabled: !!projectId,
    staleTime: 60000, // 1 minute
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Hook to create new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (project: CreateProjectRequest) => projectsApi.create(project),
    onSuccess: () => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Hook to create multiple projects in batch (wizard Step 4)
 *
 * Usage:
 * ```
 * const createBatch = useCreateProjectsBatch();
 *
 * await createBatch.mutateAsync({
 *   projects: [
 *     {
 *       name: "Project A",
 *       ict_responsible: "admin@example.com",
 *       start_date: "2026-02-01T00:00:00Z",
 *       end_date: "2026-06-30T00:00:00Z",
 *       go_live_date: "2026-07-01T00:00:00Z",
 *       scenarios: [...],
 *       user_mappings: [...]
 *     }
 *   ]
 * });
 * ```
 */
export function useCreateProjectsBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof projectsApi.createBatch>[0]) =>
      projectsApi.createBatch(data),
    onSuccess: () => {
      // Invalidate projects list to refetch
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Hook to update existing project
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      updates,
    }: {
      projectId: string;
      updates: UpdateProjectRequest;
    }) => projectsApi.update(projectId, updates),
    onSuccess: (_, variables) => {
      // Invalidate specific project and lists
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Hook to toggle project status (active/inactive)
 */
export function useToggleProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      status,
    }: {
      projectId: string;
      status: 'active' | 'inactive';
    }) => projectsApi.toggleStatus(projectId, status),
    onSuccess: (_, variables) => {
      // Invalidate specific project and lists
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Hook to delete project (soft delete)
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => projectsApi.delete(projectId),
    onSuccess: (_, projectId) => {
      // Remove from cache and refetch lists
      queryClient.removeQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}
